import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { format, startOfWeek, addDays, isSameDay } from 'date-fns';
import { FaChevronDown, FaFire } from 'react-icons/fa';
import { habitService } from '../../../services/habit.service';
import { useAuth } from '../../../context/AuthContext';
import './HabitTracker.css';

const HabitTracker = () => {
  const [habits, setHabits] = useState([]);
  const [newHabit, setNewHabit] = useState('');
  const [expandedHabits, setExpandedHabits] = useState(false);
  const { user } = useAuth();

  useEffect(() => {
    if (user) {
      const loadHabits = async () => {
        const userHabits = await habitService.getHabits(user.uid);
        setHabits(userHabits);
      };
      loadHabits();
    }
  }, [user]);

  const toggleHabit = async (habitId, date) => {
    try {
      await habitService.toggleHabitCompletion(user.uid, habitId, date);
      const updatedHabits = await habitService.getHabits(user.uid);
      setHabits(updatedHabits);
    } catch (error) {
      console.error('Error toggling habit:', error);
    }
  };

  const addHabit = async (e) => {
    e.preventDefault();
    if (!newHabit.trim()) return;

    try {
      await habitService.createHabit(user.uid, newHabit);
      const updatedHabits = await habitService.getHabits(user.uid);
      setHabits(updatedHabits);
      setNewHabit('');
    } catch (error) {
      console.error('Error adding habit:', error);
    }
  };

  const getWeekDays = () => {
    const start = startOfWeek(new Date(), { weekStartsOn: 1 });
    return Array.from({ length: 7 }, (_, i) => addDays(start, i));
  };

  const weekDays = getWeekDays();

  const getStreakMessage = (streak) => {
    if (streak >= 7) return `🔥 ${streak} day streak!`;
    if (streak >= 5) return `🔥 ${streak} days`;
    if (streak >= 3) return `🔥 ${streak} days`;
    if (streak === 2) return "🔥 2 days";
    return '';
  };

  const renderHabitRow = (habit) => (
    <div className="habit-row">
      <div className="habit-name">
        {habit.name}
        {habit.streak >= 2 && (
          <div className="streak-message">
            <FaFire className="streak-icon" />
            <span>{getStreakMessage(habit.streak)}</span>
          </div>
        )}
      </div>
      {weekDays.map((day) => {
        const isCompleted = habit.completedDates?.some(
          date => isSameDay(new Date(date), day)
        );
        return (
          <div
            key={day.toString()}
            className={`habit-check ${isCompleted ? 'completed' : ''}`}
            onClick={() => toggleHabit(habit.id, day)}
          >
            {isCompleted && '✓'}
          </div>
        );
      })}
    </div>
  );

  return (
    <div className="habit-tracker">
      <div className="habit-tracker-header">
        <div className="header-top">
          <h2>Habit Tracker</h2>
          {habits.length > 1 && (
            <button 
              className="expand-button"
              onClick={() => setExpandedHabits(!expandedHabits)}
            >
              <span>{expandedHabits ? 'Show Less' : `Show ${habits.length - 1} More`}</span>
              <motion.div
                animate={{ rotate: expandedHabits ? 180 : 0 }}
                transition={{ duration: 0.2 }}
              >
                <FaChevronDown />
              </motion.div>
            </button>
          )}
        </div>
        <form onSubmit={addHabit} className="add-habit-form">
          <input
            type="text"
            value={newHabit}
            onChange={(e) => setNewHabit(e.target.value)}
            placeholder="Add a new habit..."
            className="habit-input"
          />
          <button type="submit" className="add-habit-button">Add</button>
        </form>
      </div>

      <div className="habits-grid">
        <div className="habits-header">
          <div className="habit-name-header">Habit</div>
          {weekDays.map((day) => (
            <div key={day.toString()} className="day-header">
              {format(day, 'EEE')}
              <span className="day-number">{format(day, 'd')}</span>
            </div>
          ))}
        </div>

        {habits.length > 0 && renderHabitRow(habits[0])}
        
        <AnimatePresence>
          {expandedHabits && habits.length > 1 && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.2 }}
            >
              {habits.slice(1).map(habit => renderHabitRow(habit))}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default HabitTracker; 